import { useSelector } from 'react-redux';
import useSWR, { mutate } from 'swr';
import useSWRMutation from 'swr/mutation';
import settings from '/@/core/settings';
import { invalidateCropMonitoringQueries } from '/@/hooks/crop-monitoring/use-crop-monitoring';
import { axFetch } from '/@/store/session/actions';
import { getSessionCountry } from '/@/store/session/selectors';
import { invalidate } from '/@/utils/swr';

type ReportType = 'harvest' | 'planting' | 'application';

export const getFvGrowingSeasons = async ({ fieldId }: { fieldId: number }) => {
    const apiUrl = new URL(
        `${settings.urls.apiDomain}/core/v1/fields/me/${fieldId}/fv-growing-seasons/`,
        window.location.origin
    );

    const res = await axFetch.get(apiUrl.toString());
    return res.data;
};

export const useFvGrowingSeasons = (
    {
        fieldId,
    }: {
        fieldId?: number;
    },
    { enabled = true }: { enabled?: boolean } = { enabled: true }
) => {
    let key = null;
    if (enabled && fieldId) key = { fieldId, key: 'useFvGrowingSeasons' };

    return useSWR(key, getFvGrowingSeasons);
};

export const createCropReport = async ({
    field,
    type,
}: {
    field: number;
    type: 'harvest' | 'planting' | 'application';
}) => {
    const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/reports/me/${type}/create-all/`, window.location.origin);

    const res = await axFetch.post(apiUrl.toString(), {
        field_id: field,
    });
    return res.data;
};

export const useCreateCropReport = ({ field, type }: { field: number; type: ReportType }) => {
    let key = null;
    if (field && type) key = { key: 'useCreateCropReport', field, type };
    return useSWRMutation(key, () => createCropReport({ field, type }), {
        onSuccess: () => {
            mutate((key) => key?.fieldId === field && key?.type === type && key?.key == 'useReports');
        },
    });
};

export const getReports = async ({ fieldId, type }: { fieldId: number; type: ReportType }) => {
    const params = new URLSearchParams();
    if (fieldId) params.append('field_id', String(fieldId));

    const apiUrl = new URL(
        `${settings.urls.apiDomain}/core/v1/reports/me/${type}/?${params.toString()}`,
        window.location.origin
    );

    const res = await axFetch.get(apiUrl.toString());
    return res.data;
};

export const useReports = (
    {
        fieldId,
        type,
    }: {
        fieldId?: number;
        type: ReportType;
    },
    { enabled = true }: { enabled?: boolean } = { enabled: true }
) => {
    let key = null;
    if (enabled && fieldId && type) key = { fieldId, type, key: 'useReports' };

    return useSWR(key, getReports);
};

export const updateHumidityAtHarvestCropMonitoringResult = async ({
    cropMonitoringId,
    humidityAtHarvest,
}: {
    cropMonitoringId: number;
    humidityAtHarvest?: number;
}) => {
    const apiUrl = new URL(
        `${settings.urls.apiDomain}/core/v1/crop-monitoring/${cropMonitoringId}/result/`,
        window.location.origin
    );

    const res = await axFetch.patch(apiUrl.toString(), {
        humidityAtHarvest: humidityAtHarvest,
    });
    return res.data;
};

export const createOrUpdateCropMonitoringResult = async ({
    cropMonitoringId,
    plantingDate,
    harvestDate,
    humidityAtHarvest,
    averageYield,
    harvestReport,
    harvestResult,
}: {
    cropMonitoringId: number;
    plantingDate?: string;
    harvestDate?: string;
    humidityAtHarvest?: number;
    averageYield?: number;
    harvestReport?: number;
    harvestResult?: number;
}) => {
    const apiUrl = new URL(
        `${settings.urls.apiDomain}/core/v1/crop-monitoring/${cropMonitoringId}/result/`,
        window.location.origin
    );

    const res = await axFetch.put(apiUrl.toString(), {
        plantingDate: plantingDate,
        harvestDate: harvestDate,
        humidityAtHarvest: humidityAtHarvest,
        averageYield: averageYield,
        harvest_report: harvestReport,
        harvest_result: harvestResult,
    });
    return res.data;
};

export const useCreateOrUpdateCropMonitoringResult = ({
    cropMonitoringId,
    fieldId,
}: {
    cropMonitoringId: number;
    fieldId: number;
}) => {
    const country = useSelector(getSessionCountry);

    let key = null;
    if (cropMonitoringId) key = { key: 'useCreateCropReport', cropMonitoringId };
    return useSWRMutation(
        key,
        (
            { cropMonitoringId },
            {
                arg,
            }: {
                arg: {
                    plantingDate?: string;
                    harvestDate?: string;
                    humidityAtHarvest?: number;
                    averageYield?: number;
                    harvestReport?: number;
                    harvestResult?: number;
                };
            }
        ) => createOrUpdateCropMonitoringResult({ cropMonitoringId, ...arg }),
        {
            onSuccess: (result) => invalidateResultCropMonitorings(result, cropMonitoringId, fieldId, country.code),
        }
    );
};

/**
 * A harvest result is shared by the hybrids of the campaign (`cropMonitorings`), so a
 * change to it shows on every one of them: invalidate them all.
 */
const invalidateResultCropMonitorings = (
    result: { cropMonitorings?: number[] } | undefined,
    cropMonitoringId: number,
    fieldId: number,
    country: string
) => {
    // The field-wide keys once; only the per-CM key for every hybrid.
    invalidateCropMonitoringQueries({ cmId: cropMonitoringId, fieldId, country });
    for (const cmId of result?.cropMonitorings ?? []) {
        if (cmId !== cropMonitoringId) invalidate({ key: 'useCropMonitoringById', id: cmId });
    }
};

export const useUpdateHumidityAtHarvestCropMonitoringResult = ({
    cropMonitoringId,
    fieldId,
}: {
    cropMonitoringId: number;
    fieldId: number;
}) => {
    const country = useSelector(getSessionCountry);

    let key = null;
    if (cropMonitoringId) key = { key: 'useUpdateHumidityAtHarvestCropMonitoringResult', cropMonitoringId };
    return useSWRMutation(
        key,
        (
            { cropMonitoringId },
            {
                arg,
            }: {
                arg: {
                    humidityAtHarvest?: number;
                };
            }
        ) => updateHumidityAtHarvestCropMonitoringResult({ cropMonitoringId, ...arg }),
        {
            onSuccess: (result) => invalidateResultCropMonitorings(result, cropMonitoringId, fieldId, country.code),
        }
    );
};
